import { useMemo } from "react";
import { getHotelPayNowTotal } from "@apac/components/checkout";
import type { Amount, FiatPrice } from "@b2bportal/air-booking-api";
import { Product } from "@b2bportal/purchase-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { Prices } from "@hopper-b2b/types";
import { getProductTotalPrice } from "@hsbc/shared";
import { ZERO_FIAT_AMOUNT } from "@hsbc/shared";
import type { FlightMachineContext, LodgingMachineContext } from "@hsbc/types";
import type { State } from "xstate";

export const amountToFiatPrice = (
  amount?: Amount
): Omit<FiatPrice, "currencySymbol"> => ({
  value: amount?.amount || 0,
  currencyCode: amount?.currency || ZERO_FIAT_AMOUNT.currencyCode,
});

export const twoFractionOptions = {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
};

export const noFractionOptions = {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
};

export const defaultFractionOptions = {
  maximumFractionDigits: undefined,
  minimumFractionDigits: undefined,
};

export const roundToTwoDecimal = (value: number) => {
  return Math.round(value * 100) / 100;
};

type CheckoutState = State<FlightMachineContext | LodgingMachineContext>;
type CheckoutStateWithoutValue = Pick<CheckoutState, "context">;
type CheckoutStateWithAndWithoutValue =
  | CheckoutState
  | CheckoutStateWithoutValue;

// total price using quote breakdown if available otherwise use shopped price, except flight which is quote price only
export const productTotalSelectorByProduct = (
  product?: Product
): ((state: CheckoutStateWithAndWithoutValue) => Prices) => {
  switch (product) {
    case Product.Flight:
      // consider before quote price if needed (getFlightTotalInPrices is broken for after quote)
      return getProductTotalPrice;
    case Product.Hotel:
      return (state) =>
        getHotelPayNowTotal(
          state as Pick<State<LodgingMachineContext>, "context">
        );
    default:
      return getProductTotalPrice;
  }
};

export const useZeroAmount = (): Prices => {
  const { brand } = useI18nContext();

  return useMemo(
    () => ({
      fiat: {
        currencyCode: brand.currency.code,
        currencySymbol:
          brand.currency.symbolOverride ??
          brand.currency.symbol ??
          brand.currency.code,
        value: 0,
      },
      accountSpecific: {
        Loyalty: {
          currency: "Reward points",
          value: 0,
        },
      },
      rewards: {
        Loyalty: {
          currency: "Reward points",
          value: 0,
        },
      },
    }),
    [brand]
  );
};
